'use client';
import '@css/insurance-form/insurance-form.scss';

import {useEffect, useRef, useState} from "react";

import FormSubmissionProcessingLoader from "@components/common/ui-elements/loader/form-submission-processing-loader";
import {
   BUSINESS_ACTIVITY_OPTIONS,
   BUSINESS_CATEGORY_OPTIONS,
   TITLE_OPTIONS,
   YES_NO_CHOICE_OPTIONS
} from "@constants/dropdown-choices";
import ScrollableTextBoxBlock from "@components/common/form-blocks/scrollable/text-box-block";
import ScrollableButtonGroupBlock from "@components/common/form-blocks/scrollable/button-group-block";
import ScrollableDropdownBlock from "@components/common/form-blocks/scrollable/dropdown-block";
import SectionHeading from "@components/common/ui-elements/headings/section-heading";
import ScrollableCustomDateTimePickerBlock
   from "@components/common/form-blocks/scrollable/custom-datetime-picker-block";
import {insuranceFormOnSubmit} from "@functions/insurance-form-on-submit";
import {useGoogleReCaptcha} from "react-google-recaptcha-v3";
import UseTestData from "@components/common/use-test-data";
import {
   EmployerLiabilityInsuranceQuoteTestData as TestData
} from "@app/test-form-data/employer-liability-insurance-quote";
import {isEditableMode, isEditMode, isNewMode, useFormMode} from "@contexts/FormModeContext";
import {
   API_EMPLOYER_LIABILITY_INSURANCE_QUOTE_NEW,
   API_EMPLOYER_LIABILITY_INSURANCE_QUOTE_UPDATE
} from "@constants/next-api-endpoints";
import {checkAndAddCssClassToRefContainerChildren} from "@utils/dom";
import Link from "next/link";
import {
   DASH_LINK_EMPLOYER_LIABILITY_INSURANCE_QUOTE_EDIT,
   DASH_LINK_EMPLOYER_LIABILITY_INSURANCE_QUOTE_LIST
} from "@constants/dash-links";
import {validateEmployerLiabilityData} from "@validators/employer-liability-insurance-form/validator";
import {showValidationErrorsModal} from "@utils/modal-manager";
import {TermsAgreementBlock} from "@components/common/form-blocks/other/terms-agreement-block";
import SubmissionDetailsHeadingBlock
   from "@components/common/sub-components/insurance-form-parts/submission-details-heading-block";
import {
   BACKEND_BUSINESS_INSURANCE_QUOTE_NEW,
   BACKEND_EMPLOYER_LIABILITY_INSURANCE_QUOTE_NEW, BACKEND_EMPLOYER_LIABILITY_INSURANCE_QUOTE_UPDATE
} from "../../../../constants/laravel-backend-endpoints";

const EDIT_URL = DASH_LINK_EMPLOYER_LIABILITY_INSURANCE_QUOTE_EDIT;
const LIST_URL = DASH_LINK_EMPLOYER_LIABILITY_INSURANCE_QUOTE_LIST;

export default function EmployerLiabilityInsuranceForm({
   testData = false,
   initialFormData = {},
   firstStep = 0,
}) {
   
   const {mode} = useFormMode();

   /** Single-page form — used by server-validation “jump to” links. */
   const STEP_LIST = [{step: 0, label: "Application form"}];
   const currentStep = 0;
   
   const {executeRecaptcha} = useGoogleReCaptcha();
   
   const [useTestData, setUseTestData] = useState(testData);
   
   const [isSubmissionProcessing, setIsSubmissionProcessing] = useState(false);
   const [submissionSuccessMsg, setSubmissionSuccessMsg] = useState('');
   const [submissionErrorMsg, setSubmissionErrorMsg] = useState('');
   const [submissionProcessingMsg, setSubmissionProcessingMsg] = useState('');
   
   const formRef = useRef(null);
   const cifContainerRef = useRef(null);
   
   const [serverValidationErrors, setServerValidationErrors] = useState([]);
   
   const defaultData = {
      ...{
         
         /* Default state values ... */
         
         aboutBusiness: '',
         targetProfession: '',
         hasSecondaryTrade: '',
         businessPostCode: '',
         businessCategory: '',
         policyStartDate: '',
         title: '',
         firstName: '',
         lastName: '',
         isCorrespondenceAddressSameAsBusinessAddress: '',
         contactEmail: '',
         contactPhone: '',
         alternatePhoneNumber: '',
         haveEmployees: '',
         numberOfEmployees: '',
         insurancePaymentType: '',
         useVanForBusiness: '',
         doneBusinessOutsideUK: '',
         largestFeeIncome: '',
         businessActivities: '',
         isMemberOfAcca: '',
         hasDoneNonIntroducerFinancialServiceWork: '',
         doesWorkForListedCompanies: '',
         hasClientInFinancialServiceSector: '',
         hadClaimsOrAskedCompensationInLast5Years: '',
         isAwareOfPotentialClaimsAgainstCompany: '',
         hasOngoingLegalDispute: '',
         hasOngoingDisputeWithEmployees: '',
         wasSubjectToDisciplinaryHearing: '',
         hadInsuranceCoverTerminated: '',
         hadReceivedCountyCourtJudgmentOnDebt: '',
         wasDeclaredBankrupt: '',
         wasPartOfCompanyLiquidated: '',
         wasConvictedOfNonMotoringCriminalOffense: '',
         
         addAdditionalComments: '',
         
         termsAgreement: '',
         
      },
      ...initialFormData,
   };
   
   const [formData, setFormData] = useState(defaultData);
   
   useEffect(() => {
      if (useTestData) {
         setFormData(f => {
            return {
               ...f,
               ...TestData,
            }
         })
      } else {
        setFormData(defaultData);
      }
   }, [useTestData]);
   
   const isArrayTypeControl = (name) => {
      return [
         'contactOptions[]'
      ].includes(name);
   }
   
   const arrayTypeControlMapping = () => {
      return {
         'contactOptions[]': 'contactOptions'
      };
   }
   
   const eventHandlers = {
      onControlChange: (event, callback) => {
         
         if (typeof event !== 'object') {
            return;
         }
         
         const {name, value, checked} = event.target;
         
         if (isArrayTypeControl(name)) {
            
            let propName = arrayTypeControlMapping()[name];
            
            if (propName) {
               
               
               let existingOptions = formData[propName];
               
               if (checked) {
                  
                  existingOptions.push(value);
                  
               } else {
                  existingOptions = existingOptions.filter((item) => {
                     return item !== value;
                  });
               }
               
               setFormData({
                  ...formData,
                  [propName]: existingOptions
               });
               
            }
            
         } else {
            setFormData({
               ...formData,
               [name]: value
            });
         }
         
         
         if (typeof callback === 'function') {
            callback.apply(null);
         }
      }
   };
   
   const ServerValidationMessageList = () => {
      return (
         <div className="alert alert-danger m-3 p-3 text-left">
            <h3 className="mb-4">Please fix these issues and try again - </h3>
            
            <ol>
               {serverValidationErrors?.map((error, index) => {
                  return (
                     <li key={index}>{error}</li>
                  );
               })}
            </ol>
            
            
            <strong>Jump to - </strong>
            
            <ul>
               
               {STEP_LIST?.map((item, index) => {
                  return (
                     <li key={index}>
                        <a
                           href="#"
                           onClick={(e) => {
                              e.preventDefault();
                              backToForm({
                                 step: item.step
                              })
                           }}
                        >Step {item.step + 1}</a>
                        <span> - {item.label}</span>
                     </li>
                  );
               })}
            
            </ul>
         </div>
      )
   }
   
   const SubmissionErrorMessage = () => {
      return (
         <div className="alert alert-danger m-3 p-3 text-center">
            {submissionErrorMsg}
            <br />
            <br />
            <button type="button" className="btn btn-primary" onClick={() => {
               backToForm({step: currentStep})
            }}>
               <i className="fa fa-chevron-left" /> Back
            </button>
         
         </div>
      );
   }
   
   const SubmissionSuccessMessage = () => {
      return (
         <div className="alert alert-success p-3 m-3 text-center">
            
            {submissionSuccessMsg}
            
            <br />
            <br />
            
            {isNewMode(mode) && (
               <>
                  <button
                     type="button"
                     className="btn btn-primary" onClick={(e) => {
                     setUseTestData(false);
                     backToForm({
                        step: 0,
                     })
                  }}
                  >
                     Submit another application
                  </button>
               </>
            )}
            
            {isEditMode(mode) && (
               <div className="d-flex justify-content-center align-items-center gap-3">
                  <button
                     type="button"
                     className="btn btn-primary" onClick={(e) => {
                     setUseTestData(false);
                     backToForm({
                        step: 0,
                     })
                  }}
                  >
                     <i className="fa fa-pencil"></i> Edit again
                  </button>
                  
                  <Link
                     href={DASH_LINK_EMPLOYER_LIABILITY_INSURANCE_QUOTE_LIST}
                     className="btn btn-primary">
                     <i className="fa fa-list"></i> Applications List
                  </Link>
               </div>
            )}
         
         
         </div>
      );
   }
   
   const onSubmit = async () => {
      
      if (!isEditableMode(mode)) {
         return;
      }
      
      let validationResult = validateEmployerLiabilityData(formData);
      
      if (validationResult.length) {
         
         showValidationErrorsModal({
            validationResult: validationResult
         });
         
         return;
         
      }
      
      let endPointUrl = BACKEND_EMPLOYER_LIABILITY_INSURANCE_QUOTE_NEW;
      
      if (isEditMode(mode) && initialFormData.id) {
         endPointUrl = BACKEND_EMPLOYER_LIABILITY_INSURANCE_QUOTE_UPDATE + (initialFormData.id || '');
      }
      
      await insuranceFormOnSubmit({
         formData,
         setFormData,
         executeRecaptcha,
         setIsSubmissionProcessing,
         setSubmissionErrorMsg,
         setServerValidationErrors,
         setSubmissionProcessingMsg,
         setSubmissionSuccessMsg,
         endPointUrl: endPointUrl,
         resetForm: isNewMode(mode),
      })
   }
   
   /**
    * This method allows navigating to any step of the form.
    *
    * @param step
    */
   
   function backToForm(
      {
         step = 0,
      }
   ) {
      setIsSubmissionProcessing(false);
      setSubmissionErrorMsg('');
      setSubmissionSuccessMsg('');
      setServerValidationErrors([]);
      
      
      checkAndAddCssClassToRefContainerChildren({
         refContainer: cifContainerRef,
         selector: '.step-container',
         className: 'fade-in',
      });
   }
   
   return (
      
      <div className="cif-container" ref={cifContainerRef}>
         
         
         {isSubmissionProcessing && (
            <FormSubmissionProcessingLoader
               text={isNewMode(mode) ?
                  'Submitting new application...' :
                  'Updating application...'}
            />
         )}
         
         {!isSubmissionProcessing && serverValidationErrors.length !== 0 && (
            <ServerValidationMessageList />
         )}
         
         {!isSubmissionProcessing && submissionErrorMsg && (
            
            <SubmissionErrorMessage />
         
         )}
         
         {!isSubmissionProcessing && submissionSuccessMsg && (
            
            <SubmissionSuccessMessage />
         
         )}
         
         
         {!isSubmissionProcessing && submissionErrorMsg === '' && submissionSuccessMsg === '' && serverValidationErrors.length === 0 && (
            
            
            <form
               ref={formRef}
               className="insurance-form"
               onSubmit={(e) => {
                  e.preventDefault();
                  
                  onSubmit(e);
               }}>
               
               {isNewMode(mode) && (
                  <UseTestData
                     useTestData={useTestData}
                     setUseTestData={setUseTestData}
                     setFormData={setFormData}
                     formData={formData}
                     testData={TestData}
                  />
               )}
              
               
               <div className="row">
                  
                  
                  
                  {/*<SectionHeading
                     text="Public liability/employer liability"
                  />*/}
                  
                  <SubmissionDetailsHeadingBlock
                     formData={formData}
                     editUrl={EDIT_URL + formData.id}
                     listUrl={LIST_URL}
                  />

                  <SectionHeading text="About Your Business" headingLevel={2} />
                  
                  
                  {/* About your business */}
                  
                  
                  <ScrollableTextBoxBlock
                     containerCssClass="col-12 py-3"
                     labelText="About your business"
                     required={false}
                     controlName="aboutBusiness"
                     value={formData.aboutBusiness}
                     onChange={eventHandlers.onControlChange}
                     inputAttrs={{"maxLength": 1024}}
                     asTextArea={false}
                  />
                  
                  
                  {/* What is your specific trade/profession? */}
                  
                  
                  <ScrollableTextBoxBlock
                     containerCssClass="col-12 py-3"
                     labelText="What is your specific trade/profession?"
                     required={true}
                     controlName="targetProfession"
                     value={formData.targetProfession}
                     onChange={eventHandlers.onControlChange}
                     inputAttrs={{"maxLength": 50}}
                     asTextArea={false}
                  />
                  
                  
                  {/* Do you have a secondary trade/profession? */}
                  
                  <ScrollableButtonGroupBlock
                     containerCssClass="col-12 py-3"
                     labelText="Do you have a secondary trade/profession?"
                     required={false}
                     controlName="hasSecondaryTrade"
                     options={YES_NO_CHOICE_OPTIONS}
                     selectedValues={formData.hasSecondaryTrade}
                     onChange={eventHandlers.onControlChange}
                  />
                  
                  
                  {/* What is your business postcode? */}
                  
                  
                  <ScrollableTextBoxBlock
                     containerCssClass="col-12 py-3"
                     labelText="What is your business postcode?"
                     required={true}
                     controlName="businessPostCode"
                     value={formData.businessPostCode}
                     onChange={eventHandlers.onControlChange}
                     inputAttrs={{"maxLength": 50}}
                     asTextArea={false}
                  />
                  
                  
                  {/* Which of these categories best describes your business? */}
                  
                  <ScrollableDropdownBlock
                     controlName="businessCategory"
                     containerCssClass="col-12 py-3"
                     labelText="Which of these categories best describes your business?"
                     required={false}
                     options={BUSINESS_CATEGORY_OPTIONS}
                     selectedValues={formData.businessCategory}
                     onChange={eventHandlers.onControlChange}
                     helpText=""
                  />
                  
                  
                  <SectionHeading
                     text="Your Policy Details"
                     headingLevel={2}
                  />
                  
                  
                  {/* Policy start date */}
                  
                  
                  <ScrollableCustomDateTimePickerBlock
                     containerCssClass="col-12 py-3"
                     labelText="Policy start date"
                     required={false}
                     controlName="policyStartDate"
                     value={formData.policyStartDate}
                     onChange={eventHandlers.onControlChange}
                  />
                  
                  
                  <SectionHeading
                     text="Your Personal Details"
                     headingLevel={2}
                  />
                  
                  
                  {/* Title */}
                  
                  <ScrollableDropdownBlock
                     controlName="title"
                     containerCssClass="col-12 py-3 col-md-4"
                     labelText="Title"
                     required={true}
                     options={TITLE_OPTIONS}
                     selectedValues={formData.title}
                     onChange={eventHandlers.onControlChange}
                     helpText=""
                  />
                  
                  
                  {/* First name */}
                  
                  
                  <ScrollableTextBoxBlock
                     containerCssClass="col-12 py-3 col-md-4"
                     labelText="First name"
                     required={true}
                     controlName="firstName"
                     value={formData.firstName}
                     onChange={eventHandlers.onControlChange}
                     inputAttrs={{"maxLength": 50}}
                     asTextArea={false}
                  />
                  
                  
                  {/* Last name */}
                  
                  
                  <ScrollableTextBoxBlock
                     containerCssClass="col-12 py-3 col-md-4"
                     labelText="Last name"
                     required={true}
                     controlName="lastName"
                     value={formData.lastName}
                     onChange={eventHandlers.onControlChange}
                     inputAttrs={{"maxLength": 50}}
                     asTextArea={false}
                  />
                  
                  
                  <SectionHeading
                     text="Contact Details"
                     headingLevel={2}
                  />
                  
                  
                  {/* Is your correspondence address the same as your business address? */}
                  
                  <ScrollableButtonGroupBlock
                     containerCssClass="col-12 py-3"
                     labelText="Is your correspondence address the same as your business address?"
                     required={false}
                     controlName="isCorrespondenceAddressSameAsBusinessAddress"
                     options={YES_NO_CHOICE_OPTIONS}
                     selectedValues={formData.isCorrespondenceAddressSameAsBusinessAddress}
                     onChange={eventHandlers.onControlChange}
                  />
                  
                  
                  {/* Email address */}
                  
                  
                  <ScrollableTextBoxBlock
                     containerCssClass="col-12 py-3"
                     labelText="Email address"
                     required={true}
                     controlName="contactEmail"
                     value={formData.contactEmail}
                     onChange={eventHandlers.onControlChange}
                     inputAttrs={{"type": "email", "maxLength": 100}}
                     asTextArea={false}
                  />
                  
                  
                  {/* Telephone number */}
                  
                  
                  <ScrollableTextBoxBlock
                     containerCssClass="col-12 py-3"
                     labelText="Telephone number"
                     required={true}
                     controlName="contactPhone"
                     value={formData.contactPhone}
                     onChange={eventHandlers.onControlChange}
                     inputAttrs={{"type": "tel", "maxLength": 50}}
                     asTextArea={false}
                  />
                  
                  
                  {/* Alternative phone number */}
                  
                  
                  <ScrollableTextBoxBlock
                     containerCssClass="col-12 py-3"
                     labelText="Alternative phone number"
                     required={false}
                     controlName="alternatePhoneNumber"
                     value={formData.alternatePhoneNumber}
                     onChange={eventHandlers.onControlChange}
                     inputAttrs={{"type": "tel", "maxLength": 50}}
                     asTextArea={false}
                  />
                  
                  
                  {/* Do you have any employees? (including labour only subcontractors) */}
                  
                  <ScrollableButtonGroupBlock
                     containerCssClass="col-12 py-3"
                     labelText="Do you have any employees? (including labour only subcontractors)"
                     required={false}
                     controlName="haveEmployees"
                     options={YES_NO_CHOICE_OPTIONS}
                     selectedValues={formData.haveEmployees}
                     onChange={eventHandlers.onControlChange}
                  />
                  
                  
                  {/* Total employees (excluding partners and directors) */}
                  
                  
                  <ScrollableTextBoxBlock
                     containerCssClass="col-12 py-3"
                     labelText="Total employees (excluding partners and directors)"
                     required={false}
                     controlName="numberOfEmployees"
                     value={formData.numberOfEmployees}
                     onChange={eventHandlers.onControlChange}
                     inputAttrs={{"type": "number", "maxLength": 50}}
                     asTextArea={false}
                  />
                  
                  
                  <SectionHeading
                     text="Professional Activities & Services"
                     headingLevel={3}
                  />
                  
                  
                  {/* How do you usually pay for your insurance? */}
                  
                  
                  <ScrollableTextBoxBlock
                     containerCssClass="col-12 py-3"
                     labelText="How do you usually pay for your insurance?"
                     required={false}
                     controlName="insurancePaymentType"
                     value={formData.insurancePaymentType}
                     onChange={eventHandlers.onControlChange}
                     inputAttrs={{"maxLength": 50}}
                     asTextArea={false}
                  />
                  
                  
                  {/* Do you use a van for your business? */}
                  
                  <ScrollableButtonGroupBlock
                     containerCssClass="col-12 py-3"
                     labelText="Do you use a van for your business?"
                     required={false}
                     controlName="useVanForBusiness"
                     options={YES_NO_CHOICE_OPTIONS}
                     selectedValues={formData.useVanForBusiness}
                     onChange={eventHandlers.onControlChange}
                  />
                  
                  
                  {/* Has your business done (or do you plan to do) any work for customers based outside the UK? */}
                  
                  <ScrollableButtonGroupBlock
                     containerCssClass="col-12 py-3"
                     labelText="Has your business done (or do you plan to do) any work for customers based outside the UK?"
                     required={false}
                     controlName="doneBusinessOutsideUK"
                     options={YES_NO_CHOICE_OPTIONS}
                     selectedValues={formData.doneBusinessOutsideUK}
                     onChange={eventHandlers.onControlChange}
                  />
                  
                  
                  {/* What's the largest fee income you've earned from any one client? */}
                  
                  
                  <ScrollableTextBoxBlock
                     containerCssClass="col-12 py-3"
                     labelText="What's the largest fee income you've earned from any one client?"
                     required={false}
                     controlName="largestFeeIncome"
                     value={formData.largestFeeIncome}
                     onChange={eventHandlers.onControlChange}
                     inputAttrs={{"type": "number", "maxLength": 50}}
                     asTextArea={false}
                  />
                  
                  
                  {/* Please select which of the following activities you carry out */}
                  
                  <ScrollableDropdownBlock
                     controlName="businessActivities"
                     containerCssClass="col-12 py-3"
                     labelText="Please select which of the following activities you carry out"
                     required={false}
                     options={BUSINESS_ACTIVITY_OPTIONS}
                     selectedValues={formData.businessActivities}
                     onChange={eventHandlers.onControlChange}
                     helpText=""
                  />
                  
                  
                  {/* Are you a member of ICAEW, ACCA, CAI or ICAS? */}
                  
                  <ScrollableButtonGroupBlock
                     containerCssClass="col-12 py-3"
                     labelText="Are you a member of ICAEW, ACCA, CAI or ICAS?"
                     required={false}
                     controlName="isMemberOfAcca"
                     options={YES_NO_CHOICE_OPTIONS}
                     selectedValues={formData.isMemberOfAcca}
                     onChange={eventHandlers.onControlChange}
                  />
                  
                  
                  {/* Other than as an introducer, have or do you intend to carry out any investment or financial services work? */}
                  
                  <ScrollableButtonGroupBlock
                     containerCssClass="col-12 py-3"
                     labelText="Other than as an introducer, have or do you intend to carry out any investment or financial services work?"
                     required={false}
                     controlName="hasDoneNonIntroducerFinancialServiceWork"
                     options={YES_NO_CHOICE_OPTIONS}
                     selectedValues={formData.hasDoneNonIntroducerFinancialServiceWork}
                     onChange={eventHandlers.onControlChange}
                  />
                  
                  
                  {/* Do you work for listed companies? */}
                  
                  <ScrollableButtonGroupBlock
                     containerCssClass="col-12 py-3"
                     labelText="Do you work for listed companies?"
                     required={false}
                     controlName="doesWorkForListedCompanies"
                     options={YES_NO_CHOICE_OPTIONS}
                     selectedValues={formData.doesWorkForListedCompanies}
                     onChange={eventHandlers.onControlChange}
                  />
                  
                  
                  {/* Do you do work for clients in the financial services sector? */}
                  
                  <ScrollableButtonGroupBlock
                     containerCssClass="col-12 py-3"
                     labelText="Do you do work for clients in the financial services sector?"
                     required={false}
                     controlName="hasClientInFinancialServiceSector"
                     options={YES_NO_CHOICE_OPTIONS}
                     selectedValues={formData.hasClientInFinancialServiceSector}
                     onChange={eventHandlers.onControlChange}
                  />
                  
                  
                  <SectionHeading
                     text="Claims and Risk History"
                     headingLevel={3}
                  />
                  <p className="col-12 text-body-secondary">
                     Please either agree or disagree with the following statements.
                  </p>
                  
                  
                  {/* Have you made any claims (whether insured or not) or has anyone asked you for compensation in the last 5 years? */}
                  
                  <ScrollableButtonGroupBlock
                     containerCssClass="col-12 py-3"
                     labelText="Have you made any claims (whether insured or not) or has anyone asked you for compensation in the last 5 years?"
                     required={false}
                     controlName="hadClaimsOrAskedCompensationInLast5Years"
                     options={YES_NO_CHOICE_OPTIONS}
                     selectedValues={formData.hadClaimsOrAskedCompensationInLast5Years}
                     onChange={eventHandlers.onControlChange}
                  />
                  
                  
                  {/* Are you aware of any circumstances over the last 5 years that could give rise to a claim being made against you or the company? */}
                  
                  <ScrollableButtonGroupBlock
                     containerCssClass="col-12 py-3"
                     labelText="Are you aware of any circumstances over the last 5 years that could give rise to a claim being made against you or the company?"
                     required={false}
                     controlName="isAwareOfPotentialClaimsAgainstCompany"
                     options={YES_NO_CHOICE_OPTIONS}
                     selectedValues={formData.isAwareOfPotentialClaimsAgainstCompany}
                     onChange={eventHandlers.onControlChange}
                  />
                  
                  
                  {/* Does the company have any current or ongoing legal or contractual disputes? */}
                  
                  <ScrollableButtonGroupBlock
                     containerCssClass="col-12 py-3"
                     labelText="Does the company have any current or ongoing legal or contractual disputes?"
                     required={false}
                     controlName="hasOngoingLegalDispute"
                     options={YES_NO_CHOICE_OPTIONS}
                     selectedValues={formData.hasOngoingLegalDispute}
                     onChange={eventHandlers.onControlChange}
                  />
                  
                  
                  {/* Are there any ongoing legal disputes with current or former employees? */}
                  
                  <ScrollableButtonGroupBlock
                     containerCssClass="col-12 py-3"
                     labelText="Are there any ongoing legal disputes with current or former employees?"
                     required={false}
                     controlName="hasOngoingDisputeWithEmployees"
                     options={YES_NO_CHOICE_OPTIONS}
                     selectedValues={formData.hasOngoingDisputeWithEmployees}
                     onChange={eventHandlers.onControlChange}
                  />
                  
                  
                  {/* Have you been subject to any disciplinary hearing or investigation undertaken by your professional body? */}
                  
                  <ScrollableButtonGroupBlock
                     containerCssClass="col-12 py-3"
                     labelText="Have you been subject to any disciplinary hearing or investigation undertaken by your professional body?"
                     required={false}
                     controlName="wasSubjectToDisciplinaryHearing"
                     options={YES_NO_CHOICE_OPTIONS}
                     selectedValues={formData.wasSubjectToDisciplinaryHearing}
                     onChange={eventHandlers.onControlChange}
                  />
                  
                  
                  {/* Has anyone with control over or a financial interest in the business, including you, your family, or any partner/principal/director, had any insurance proposal declined, renewal refused or cover terminated for any reason? */}
                  
                  <ScrollableButtonGroupBlock
                     containerCssClass="col-12 py-3"
                     labelText="Has anyone with control over or a financial interest in the business, including you, your family, or any partner/principal/director, had any insurance proposal declined, renewal refused or cover terminated for any reason?"
                     required={false}
                     controlName="hadInsuranceCoverTerminated"
                     options={YES_NO_CHOICE_OPTIONS}
                     selectedValues={formData.hadInsuranceCoverTerminated}
                     onChange={eventHandlers.onControlChange}
                  />
                  
                  
                  {/* Has anyone with a financial interest in the business, including you, your family, or any partner/principal/director, received a County Court Judgement (CCJ) regarding debt (either as an individual or in connection with a business)? */}
                  
                  <ScrollableButtonGroupBlock
                     containerCssClass="col-12 py-3"
                     labelText="Has anyone with a financial interest in the business, including you, your family, or any partner/principal/director, received a County Court Judgement (CCJ) regarding debt (either as an individual or in connection with a business)?"
                     required={false}
                     controlName="hadReceivedCountyCourtJudgmentOnDebt"
                     options={YES_NO_CHOICE_OPTIONS}
                     selectedValues={formData.hadReceivedCountyCourtJudgmentOnDebt}
                     onChange={eventHandlers.onControlChange}
                  />
                  
                  
                  {/* Has anyone with control over or a financial interest in the business, including you, your family, or any partner/principal/director, been declared bankrupt or been granted an Individual Voluntary Arrangement (IVA)? */}
                  
                  <ScrollableButtonGroupBlock
                     containerCssClass="col-12 py-3"
                     labelText="Has anyone with control over or a financial interest in the business, including you, your family, or any partner/principal/director, been declared bankrupt or been granted an Individual Voluntary Arrangement (IVA)?"
                     required={false}
                     controlName="wasDeclaredBankrupt"
                     options={YES_NO_CHOICE_OPTIONS}
                     selectedValues={formData.wasDeclaredBankrupt}
                     onChange={eventHandlers.onControlChange}
                  />
                  
                  
                  {/* Has anyone with control over or a financial interest in the business, including you, your family, or any partner/principal/director, been a director of a company which went into liquidation? */}
                  
                  <ScrollableButtonGroupBlock
                     containerCssClass="col-12 py-3"
                     labelText="Has anyone with control over or a financial interest in the business, including you, your family, or any partner/principal/director, been a director of a company which went into liquidation?"
                     required={false}
                     controlName="wasPartOfCompanyLiquidated"
                     options={YES_NO_CHOICE_OPTIONS}
                     selectedValues={formData.wasPartOfCompanyLiquidated}
                     onChange={eventHandlers.onControlChange}
                  />
                  
                  
                  {/* Has anyone with control over or a financial interest in the business, including you, your family, or any partner/principal/director, been convicted of any criminal offence other than a motoring offence or an offence that is now considered "spent" under the Rehabilitation of Offenders Act 1974? */}
                  
                  <ScrollableButtonGroupBlock
                     containerCssClass="col-12 py-3"
                     labelText={`Has anyone with control over or a financial interest in the business, including you, your family, or any partner/principal/director, been convicted of any criminal offence other than a motoring offence or an offence that is now considered "spent" under the Rehabilitation of Offenders Act 1974?`}
                     required={false}
                     controlName="wasConvictedOfNonMotoringCriminalOffense"
                     options={YES_NO_CHOICE_OPTIONS}
                     selectedValues={formData.wasConvictedOfNonMotoringCriminalOffense}
                     onChange={eventHandlers.onControlChange}
                  />
               
               
               </div>
               
               
               <div className="row">
                  
                  <SectionHeading
                     text="Additional comments"
                  />
                  
                  <ScrollableTextBoxBlock
                     containerCssClass="col-12 py-3 "
                     labelText="Additional comments"
                     required={false}
                     controlName="addAdditionalComments"
                     value={formData.addAdditionalComments}
                     onChange={eventHandlers.onControlChange}
                     inputAttrs={{
                        maxLength: 1024,
                        rows: 5
                     }}
                     asTextArea={true}
                  />
               
               </div>
               
               <div className="row">
                  
                  <TermsAgreementBlock
                     controlName={"termsAgreement"}
                     controlIdPrefix={"termsAgreement-"}
                     required={true}
                     onChange={eventHandlers.onControlChange}
                     selectedValue={formData.termsAgreement}
                     labelText={"Terms agreement"}
                     isHidden={false}
                  />
               
               </div>
               
               {isEditableMode(mode) && (
                  <div className="row mt-5">
                     <div className="col d-flex justify-content-center">
                        
                        
                        <button
                           id="btn-step3-next"
                           type="button"
                           className="btn btn-success btn-steps mb-3 min-width-200px"
                           disabled={formData.termsAgreement !== 'Yes'}
                           onClick={onSubmit}
                        >
                           <i className="fa fa-fw fa-save" /> {isEditMode(mode) ? 'Save' : 'Submit'}
                        </button>
                     </div>
                  </div>
               )}
               
               
            
            </form>
         
         
         )}
         
         {/*<PrettifiedJson
            data={formData}
         />*/}
         
      </div>
   );
   
}